import moment, { type Moment } from "moment";
import type { TIconNames } from "../basic/phaseSixIcon/PhaseSixIcon";

// Per-activity visibility state surfaced on activity cards (Verborgen / Geplant / Sichtbar).
// Distinct from `SchoolClassVisibilities` (PUBLIC/PRIVATE), which controls class-level access.
export enum ActivityVisibility {
    HIDDEN = "HIDDEN",
    PLANNED = "PLANNED",
    VISIBLE = "VISIBLE",
}

export const ACTIVITY_VISIBILITY_ICONS: Record<ActivityVisibility, TIconNames> = {
    [ActivityVisibility.HIDDEN]: "eye-closed",
    [ActivityVisibility.PLANNED]: "calendar-cross",
    [ActivityVisibility.VISIBLE]: "eye",
};

export interface ActivityVisibilityResult {
    visibility: ActivityVisibility;
    plannedDate: Moment | null;
}

// --- Tests (epoch ms visibilityDate) ---

export const resolveTestActivityVisibility = (visibilityDate: number | null | undefined): ActivityVisibilityResult => {
    if (visibilityDate == null) return { visibility: ActivityVisibility.HIDDEN, plannedDate: null };
    if (visibilityDate > Date.now())
        return { visibility: ActivityVisibility.PLANNED, plannedDate: moment(visibilityDate) };
    return { visibility: ActivityVisibility.VISIBLE, plannedDate: null };
};

// A planned date can slip into the past while the teacher dwells in the share popup. Rather than
// writing a past visibility date, it collapses to "share now". Caller passes `now` to avoid drift
// between validation and submission.
export const clampPlannedDate = (plannedDate: Moment, now: Moment): Moment =>
    plannedDate.isAfter(now) ? plannedDate : now;

export const toVisibilityDateMs = (plannedDate: Moment | null, now: Moment): number | null => {
    if (!plannedDate) return null;
    return clampPlannedDate(plannedDate, now).valueOf();
};

export const visibilityToMs = (visibility: ActivityVisibility, plannedDate: Moment | null): number | null => {
    if (visibility === ActivityVisibility.HIDDEN) return null;
    if (visibility === ActivityVisibility.PLANNED) return toVisibilityDateMs(plannedDate, moment());
    return moment().valueOf();
};

// --- Vocab trainings (ISO visibilityDate) ---

const getVocabActivityVisibility = (visibilityDate: Moment | null): ActivityVisibility => {
    if (!visibilityDate) return ActivityVisibility.HIDDEN;
    if (visibilityDate.isSameOrBefore(moment())) return ActivityVisibility.VISIBLE;
    return ActivityVisibility.PLANNED;
};

export const toVisibilityDate = (visibility: ActivityVisibility, plannedDate: Moment | null): Moment | null => {
    if (visibility === ActivityVisibility.HIDDEN) return null;
    if (visibility === ActivityVisibility.PLANNED) return plannedDate ? clampPlannedDate(plannedDate, moment()) : null;
    return moment();
};

// Accepts epoch ms (current write format) as well as ISO strings from legacy records.
export const resolveVocabActivityVisibility = (
    visibilityDate: number | string | null | undefined
): ActivityVisibilityResult => {
    const parsed = visibilityDate != null ? moment(visibilityDate) : null;
    const plannedDate = parsed?.isValid() ? parsed : null;
    return { visibility: getVocabActivityVisibility(plannedDate), plannedDate };
};
